import React, { MouseEventHandler, PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import MuiButton, { ButtonClasses as MuiButtonClasses } from "@mui/material/Button";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Loader } from "@/uiKit/atoms/Loader.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";

type ButtonAction =
  | {
      onClick?: MouseEventHandler<HTMLButtonElement>;
    }
  | {
      type: "submit";
    }
  | {
      component: "a";
      target?: "_blank";
      href: string;
    };

export type BaseButtonProps = PropsWithChildren<
  {
    variant: "contained" | "outlined" | "text";
    "data-action": string;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    pending?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    form?: string;
  } & ButtonAction
>;

type ButtonClasses = StyleClasses<keyof MuiButtonClasses>;

export type ButtonProps = AtomProps<BaseButtonProps, ButtonClasses>;

/**
 * Button is a React functional component that renders a Material-UI Button.
 *
 * It restricts a MUI Button to a specific set of props:
 */
export const Button: React.FC<ButtonProps> = ({
  className,
  children,
  pending,
  startIcon,
  endIcon,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);

  return (
    <MuiButton
      {...props}
      startIcon={startIcon}
      endIcon={endIcon}
      loading={pending}
      loadingIndicator={<Loader size="M" />}
      className={cx(
        {
          [styles.button]: true,
          [styles.iconOnly]: !children && (!!startIcon || !!endIcon),
        },
        className,
      )}
    >
      {children}
    </MuiButton>
  );
};

const makeStyles = (theme: Theme) => ({
  button: css({
    padding: theme.spacing(2, 6),
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
    "&:active": {
      boxShadow: "none",
    },
    "&:focus": {
      boxShadow: "none",
    },
    "&.MuiButton-text": {
      color: theme.palette.text.primary,
    },
  }),
  iconOnly: css({
    minWidth: 0,
    padding: theme.spacing(4),
    "& .MuiButton-icon": {
      margin: 0,
    },
  }),
});
